import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2, Minimize2 } from "lucide-react";
import { useCanvasContext } from "./WorkflowCanvasContext";
import { useWorkflow } from "../../../context/WorkflowContext";
import { useWorkflowGeneration } from "../../../context/WorkflowGenerationContext";
import { getNodeRegistryEntry } from "./nodeRegistry";
import ParametersTab from "./ndv/ParametersTab";
import CodeTab from "./ndv/CodeTab";
import InputTab from "./ndv/InputTab";
import OutputTab from "./ndv/OutputTab";
import PreviewTab from "./ndv/PreviewTab";

const DEFAULT_TABS = [
    { id: "params", label: "Params" },
    { id: "code", label: "Code" },
    { id: "input", label: "Input" },
    { id: "output", label: "Output" },
];

const WEB_TABS = [
    { id: "preview", label: "Preview" },
    { id: "params", label: "Params" },
    { id: "code", label: "Code" },
    { id: "output", label: "Output" },
];

const SLIDE_VARIANTS = {
    hidden: { x: "100%", opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { type: "spring", damping: 28, stiffness: 300 } },
    exit: { x: "100%", opacity: 0, transition: { duration: 0.2 } },
};

const EXPAND_VARIANTS = {
    collapsed: { width: 420 },
    expanded: { width: "100%", transition: { type: "spring", damping: 25, stiffness: 300 } },
};

export default function NodeDetailOverlay() {
    const { selectedNodeId, setSelectedNodeId, nodes } = useCanvasContext();
    const { steps } = useWorkflow();
    const { setSelectedCanvasStepId } = useWorkflowGeneration();
    const [activeTab, setActiveTab] = useState("params");
    const [isExpanded, setIsExpanded] = useState(false);
    const isExpandedRef = useRef(isExpanded);
    useEffect(() => { isExpandedRef.current = isExpanded; }, [isExpanded]);

    // Sync selected step to WorkflowGenerationContext for AI awareness
    useEffect(() => {
        const isTriggerNode = selectedNodeId?.startsWith("trigger-");
        setSelectedCanvasStepId(isTriggerNode ? null : selectedNodeId ?? null);
        return () => setSelectedCanvasStepId(null);
    }, [selectedNodeId, setSelectedCanvasStepId]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                if (isExpandedRef.current) { setIsExpanded(false); return; }
                setSelectedNodeId(null);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [setSelectedNodeId]);

    const node = useMemo(() => nodes.find((n) => n.id === selectedNodeId), [nodes, selectedNodeId]);
    const step = useMemo(() => steps.find((s) => s.id === selectedNodeId), [steps, selectedNodeId]);
    const isTrigger = selectedNodeId?.startsWith("trigger-");

    const isWebStep = step?.config?.language === "html" || step?.config?.language === "react";
    const tabs = isWebStep ? WEB_TABS : DEFAULT_TABS;

    useEffect(() => {
        setActiveTab(isWebStep ? "preview" : "params");
    }, [selectedNodeId, isWebStep]);

    const toggleExpand = useCallback(() => setIsExpanded((prev) => !prev), []);
    const close = useCallback(() => setSelectedNodeId(null), [setSelectedNodeId]);

    if (!node) return null;
    if (isTrigger || !step) return null;

    const entry = getNodeRegistryEntry(node.data.icon);
    const Icon = entry.icon;

    return (
        <motion.div
            key={selectedNodeId}
            className="absolute top-0 right-0 h-full bg-white border-l border-gray-200 shadow-lg z-20 flex flex-col"
            variants={SLIDE_VARIANTS}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ width: isExpanded ? "100%" : 420 }}
            layout
        >
            <OverlayHeader entry={entry} Icon={Icon} node={node} isExpanded={isExpanded} onToggle={toggleExpand} onClose={close} />
            <div className="flex border-b border-gray-200">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                            activeTab === tab.id ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
                {activeTab === "preview" && <PreviewTab step={step} />}
                {activeTab === "params" && <ParametersTab step={step} />}
                {activeTab === "code" && <CodeTab step={step} isExpanded={isExpanded} />}
                {activeTab === "input" && <InputTab step={step} />}
                {activeTab === "output" && <OutputTab step={step} />}
            </div>
        </motion.div>
    );
}

function OverlayHeader({ entry, Icon, node, isExpanded, onToggle, onClose }) {
    return (
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
            <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: entry.color + "15" }}>
                <Icon size={14} style={{ color: entry.color }} />
            </div>
            <span className="text-sm font-medium text-gray-900 flex-1 truncate">{node.data.title || node.data.label}</span>
            <button onClick={onToggle} className="p-1 hover:bg-gray-100 rounded" title={isExpanded ? "Collapse" : "Expand"}>
                {isExpanded ? <Minimize2 size={16} className="text-gray-500" /> : <Maximize2 size={16} className="text-gray-500" />}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
                <X size={16} className="text-gray-500" />
            </button>
        </div>
    );
}
